import React, { useEffect, useState } from "react";
import { IconButton, Avatar, Box, Typography, Paper } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faPencil, faCheck, faRocket, faUsers } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Button,
} from "@mui/material";
import { isFieldDisabled } from "../../components/reusableComponent/FieldDisabled";
import {
  useMeeQuery,
  useUsersDynamicFieldsQuery,
} from "../../Services/settingsAPIs/CommonAPI";
import { PhoneInput } from "react-international-phone";
import { toast } from "react-toastify";
import { useUpdateUserMutation } from "../../Services/user/Users";
import { capitalizeFirstLetter } from "../../components/reusableComponent/CapitalLetter";
import { useNavigate } from "react-router-dom";

interface ErrorState {
  [key: string]: string;
}

const TEAM_PILL_COLORS = [
  "#E3F2FD",
  "#F3E5F5",
  "#E8E8E8",
  "#E8F5E9",
  "#FFF3E0",
  "#FCE4EC",
  "#E1F5FE",
];

const MyProfile: React.FC = () => {
  const { data: userData, refetch } = useMeeQuery({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [dynamicFields, setDynamicFields] = useState({});
  const [dynamicData, setDynamicData] = useState<any[]>([]);
  const { data: UsersDynamicFields } = useUsersDynamicFieldsQuery({});
  const [errors, setErrors] = useState({});
  const [theTeams, setTheTeams] = useState<any[]>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const isAdmin = localStorage.getItem("user_role");
  const [initialDynamicFields, setInitialDynamicFields] = useState({});
  const [updateUser] = useUpdateUserMutation(userData);

  const handleInputChange = (key: string, value: any) => {
    setDynamicFields((prevFields) => ({
      ...prevFields,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (UsersDynamicFields) {
      setDynamicData(UsersDynamicFields);
    }
  }, [UsersDynamicFields]);

  useEffect(() => {
    if (userData?.dynamic_fields) {
      setDynamicFields(userData.dynamic_fields);
      setInitialDynamicFields(userData.dynamic_fields); //For Comparison
    }
  }, [userData?.dynamic_fields]);

  useEffect(() => {
    if (
      firstName !== userData?.first_name ||
      lastName !== userData?.last_name
    ) {
      setIsButtonDisabled(false);
    }
  }, [firstName, lastName]);

  useEffect(() => {
    if (userData) {
      setTheTeams(userData.teams || []);
      setDynamicFields(userData?.dynamic_fields || {});
      setFirstName(userData?.first_name || "");
      setLastName(userData?.last_name || "");
      setProfileImage(userData?.profileImage || null);
      setIsLoading(false);
    }
  }, [userData]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const editDispositionHandler = () => {
    setIsEnabled(!isEnabled);
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<{ value: any }>,
    key: string
  ) => {
    setErrors({});
    setDynamicFields((prevFields) => ({
      ...prevFields,
      [key]: event.target.value,
    }));
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 120,
      },
    },
  };

  const validateForm = () => {
    const newErrors: ErrorState = {};

    dynamicData.forEach((field: any) => {
      const fieldValue = dynamicFields[field.label];

      if (field.is_required && !fieldValue) {
        newErrors[field.label] = `${field.label} is required`;
      }

      if (field.is_required && field.field_type === "tel-phone" && fieldValue) {
        const isValidPhone =
          /^(?:\+?\d{1,3})?[ -]?\(?\d{1,4}?\)?[ -]?\d{1,4}[ -]?\d{1,4}(?: x\d{1,4})?$/.test(
            fieldValue
          );
        if (!isValidPhone) {
          newErrors[
            field.label
          ] = `${field.label} must be a valid phone number`;
        }
      }

      if (
        field?.field_type === "email" &&
        field.is_required &&
        !field.is_masked
      ) {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(dynamicFields[field.label])) {
          newErrors[field.label] = "Invalid email format";
        }
      }

      if (
        field.field_type === "check-box" &&
        field.is_required &&
        !fieldValue.length
      ) {
        newErrors[field.label] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill all required fields.");
      setIsButtonDisabled(true);
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 3000);
      return;
    }

    setIsButtonDisabled(false);

    const dynamicFieldData = Object.keys(dynamicFields).reduce((acc, key) => {
      const field = dynamicData.find((field) => field.label === key);

      const isFieldEditableForRole = isAdmin === "admin" || field?.is_editable;

      // Get initial unmasked field value and current unmasked field value
      const initialFieldValue = initialDynamicFields[key]; // Unmasked initial value
      const currentFieldValue = dynamicFields[key]; // Current value (unmasked on focus)

      // Only include the field if it's editable and has been changed (unmasked values are compared)
      if (isFieldEditableForRole && currentFieldValue !== initialFieldValue) {
        // Handle date formatting
        if (field?.field_type === "date") {
          const formattedDate = new Date(currentFieldValue)
            .toISOString()
            .replace("Z", "+00:00");
          acc[key] = formattedDate;
        } else {
          acc[key] = currentFieldValue; // Store the unmasked value
        }
      }

      return acc;
    }, {});

    try {
      const obj = {
        id: userData.id,
        teams: theTeams.map((team) => team.id),
        dynamic_fields: dynamicFieldData, // Only edited fields included
        module_permissions: [],
        role: userData.role,
        first_name: firstName,
        last_name: lastName,
        avatar: profileImage,
      };

      const response = await updateUser({ body: obj, id: userData.id });

      if (response?.data) {
        toast.success("Profile updated successfully!");
        refetch();
      } else if (response?.error) {
        const error = response.error;
        toast.error(error);
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 3000);
    }
  };

  const getDataTimeFormatted = (date) => {
    if (date && date.length > 0) {
      const dateObj = new Date(date);

      // Manually format the date to ensure no timezone conversion issues
      const year = dateObj.getUTCFullYear();
      const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0"); // Months are zero-based
      const day = String(dateObj.getUTCDate()).padStart(2, "0");
      const hours = String(dateObj.getUTCHours()).padStart(2, "0");
      const minutes = String(dateObj.getUTCMinutes()).padStart(2, "0");

      const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      return formattedDate;
    } else {
      return "";
    }
  };

  const filterNumericInput = (value) => {
    return value.replace(/[^0-9.]/g, "");
  };

  const handleMask = (label) => {
    const value = dynamicFields[label];
    if (value) {
      setDynamicFields((prev) => ({
        ...prev,
        [label]: value,
      }));
    }
  };

  const handleUnmask = (label) => {
    const value = initialDynamicFields[label] || dynamicFields[label];
    if (value) {
      setDynamicFields((prev) => ({
        ...prev,
        [label]: value,
      }));
    }
  };
  const navigate = useNavigate(); // Initialize useNavigate
  const handleBackButtonClick = () => {
    navigate(-1); // Navigate back to the previous page
  };

  const planName = localStorage.getItem("planName") || "";

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Box
          sx={{
            py: 2,
            px: "30px",
            minHeight: "100%",
            background: "linear-gradient(180deg, #f8f9fa 0%, #f0f0f2 100%)",
            backgroundImage: "radial-gradient(#d0d0d0 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              alignItems: "stretch",
              width: "100%",
            }}
          >
            {/* Left column: Profile + Teams */}
            <Box sx={{ flex: "1 1 0", minWidth: 280 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Profile Details card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: "14px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 3,
                    }}
                  >
                    <Box>
                      <button
                        type="button"
                        className="btn btn-link p-0 mb-1"
                        onClick={handleBackButtonClick}
                        style={{
                          fontSize: "14px",
                          color: "#6c757d",
                          cursor: "pointer",
                          textDecoration: "none",
                        }}
                      >
                        <i
                          className="bi bi-arrow-90deg-left"
                          style={{ fontSize: "18px", marginRight: 4 }}
                        />
                        Back
                      </button>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ fontSize: "1.5rem", mt: 0.5 }}
                      >
                        {firstName} {lastName}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {userData?.role
                          ? capitalizeFirstLetter(String(userData.role))
                          : "Member"}{" "}
                        at {userData?.organization_name || "Organization"}
                      </Typography>
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={editDispositionHandler}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: isEnabled ? "#6366f1" : "#6c757d",
                        fontSize: "0.875rem",
                        "&:hover": { color: "#4f46e5" },
                      }}
                    >
                      <FontAwesomeIcon icon={faPencil} style={{ fontSize: 14 }} />
                      Edit Profile
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "center", sm: "flex-start" },
                      gap: 3,
                      mb: 3,
                    }}
                  >
                    <Box position="relative" sx={{ flexShrink: 0 }}>
                      <Avatar
                        src={profileImage || userData?.avatar}
                        sx={{
                          width: 120,
                          height: 120,
                          border: "3px solid #fff",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      />
                      <label htmlFor="imageUpload">
                        <IconButton
                          component="span"
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            bgcolor: "white",
                            width: 36,
                            height: 36,
                            boxShadow: 1,
                            "&:hover": { bgcolor: "#f5f5f5" },
                          }}
                          onClick={() => {
                            if (!isEnabled) {
                              toast.error("Please enable edit mode first.");
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faCamera} style={{ fontSize: 16 }} />
                        </IconButton>
                      </label>
                      <input
                        type="file"
                        id="imageUpload"
                        accept=".png, .jpg, .jpeg"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                        disabled={!isEnabled}
                      />
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "20px",
                          bgcolor: "#dcfce7",
                          color: "#166534",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: "#22c55e",
                          }}
                        />
                        Active
                      </Box>
                      {isAdmin === "admin" && (
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "20px",
                            bgcolor: "#f1f5f9",
                            color: "#475569",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          Admin
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
                      <InputLabel
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          color: "#64748b",
                          mb: 0.5,
                          display: "block",
                        }}
                      >
                        FIRST NAME
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={!isEnabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#fff",
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
                      <InputLabel
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          color: "#64748b",
                          mb: 0.5,
                          display: "block",
                        }}
                      >
                        LAST NAME
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={!isEnabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#fff",
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: "1 1 100%", minWidth: 0 }}>
                      <InputLabel
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          color: "#64748b",
                          mb: 0.5,
                          display: "block",
                        }}
                      >
                        USERNAME/EMAIL
                      </InputLabel>
                      <TextField
                        type="email"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={userData?.username}
                        disabled
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#f8fafc",
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  {isEnabled && (
                    <div className="row" style={{ marginTop: 16 }}>
                      {dynamicData?.map((field: any) => (
                        <React.Fragment key={field?.label}>
                          <div key={field.label} className="col-xl-4 col-md-6 col-12 mb-3">
                            <InputLabel
                              htmlFor={`dynamic_fields.${field.label}`}
                              sx={{
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                letterSpacing: "0.05em",
                                color: "#64748b",
                                mb: 0.5,
                                display: "block",
                              }}
                            >
                              {field.label}
                              {field.is_required && (
                                <span className="required" />
                              )}
                            </InputLabel>
                            {field.field_type === "text" && (
                              <TextField
                                type="text"
                                id={`dynamic_fields.${field.label}`}
                                variant="outlined"
                                size="small"
                                disabled={isFieldDisabled(field?.is_editable)}
                                fullWidth
                                value={dynamicFields[field.label] || ""}
                                onChange={(e) =>
                                  handleInputChange(field.label, e.target.value)
                                }
                                required={field.is_required}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                  },
                                }}
                              />
                            )}
                            {field.field_type === "email" && (
                              <TextField
                                type="email"
                                id={`dynamic_fields.${field.label}`}
                                disabled={isFieldDisabled(field?.is_editable)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dynamicFields[field.label] || ""}
                                onChange={(e) =>
                                  handleInputChange(field.label, e.target.value)
                                }
                                required={field.is_required}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                  },
                                }}
                              />
                            )}
                            {field.field_type === "number" && (
                              <TextField
                                type="text"
                                id={`dynamic_fields.${field.label}`}
                                variant="outlined"
                                size="small"
                                fullWidth
                                disabled={isFieldDisabled(field?.is_editable)}
                                value={dynamicFields[field.label] || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    field.label,
                                    filterNumericInput(e.target.value)
                                  )
                                }
                                onFocus={() => handleUnmask(field.label)}
                                onBlur={() => handleMask(field.label)}
                                required={field.is_required}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                  },
                                }}
                              />
                            )}
                            {field.field_type === "date" && (
                              <TextField
                                type="date"
                                id={`dynamic_fields.${field.label}`}
                                variant="outlined"
                                size="small"
                                disabled={isFieldDisabled(field?.is_editable)}
                                fullWidth
                                value={dynamicFields[field.label] || ""}
                                onChange={(e) =>
                                  handleInputChange(field.label, e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                                required={field.is_required}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                  },
                                }}
                              />
                            )}
                            {field.field_type === "time" && (
                              <TextField
                                type="time"
                                id={`dynamic_fields.${field.label}`}
                                variant="outlined"
                                size="small"
                                fullWidth
                                disabled={isFieldDisabled(field?.is_editable)}
                                value={dynamicFields[field.label] || ""}
                                onChange={(e) =>
                                  handleInputChange(field.label, e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                                required={field.is_required}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                  },
                                }}
                              />
                            )}
                            {field.field_type === "date-time" && (
                              <TextField
                                type="datetime-local"
                                id={`dynamic_fields.${field.label}`}
                                variant="outlined"
                                size="small"
                                disabled={isFieldDisabled(field?.is_editable)}
                                fullWidth
                                defaultValue={getDataTimeFormatted(
                                  dynamicFields[field.label]
                                )}
                                onChange={(e) =>
                                  handleInputChange(field.label, e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                                required={field.is_required}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                  },
                                }}
                              />
                            )}
                            {field.field_type === "text-area" && (
                              <TextField
                                id={`dynamic_fields.${field.label}`}
                                variant="outlined"
                                size="small"
                                disabled={isFieldDisabled(field?.is_editable)}
                                fullWidth
                                multiline
                                value={dynamicFields[field.label] || ""}
                                onChange={(e) =>
                                  handleInputChange(field.label, e.target.value)
                                }
                                required={field.is_required}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                  },
                                }}
                              />
                            )}
                            {field.field_type === "drop-down" && (
                              <FormControl fullWidth size="small">
                                <Select
                                  labelId={`select-label-${field.label}`}
                                  id={`select-${field.label}`}
                                  value={dynamicFields[field.label] || ""}
                                  disabled={isFieldDisabled(field?.is_editable)}
                                  onChange={(e) =>
                                    handleInputChange(field.label, e.target.value)
                                  }
                                  required={field.is_required}
                                  sx={{ borderRadius: "10px" }}
                                >
                                  <MenuItem selected disabled>
                                    Select
                                  </MenuItem>
                                  {field?.choices?.map((option: any) => (
                                    <MenuItem key={option.id} value={option.choice}>
                                      {option.choice}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            {field.field_type === "check-box" && (
                              <FormControl fullWidth size="small">
                                <Select
                                  labelId={`checkbox-label-${field.label}`}
                                  id={`checkbox-${field.label}`}
                                  disabled={isFieldDisabled(field?.is_editable)}
                                  required={field.is_required}
                                  multiple
                                  value={dynamicFields[field.label] || []}
                                  onChange={(e: any) =>
                                    handleCheckboxChange(e, field.label)
                                  }
                                  renderValue={(selected) => {
                                    const selectedLabels = (
                                      field?.multiple_choices || []
                                    )
                                      .filter((choice) =>
                                        selected.includes(String(choice.id))
                                      )
                                      .map((choice) => choice.choice);
                                    return selectedLabels.join(", ");
                                  }}
                                  sx={{ borderRadius: "10px" }}
                                >
                                  {field?.multiple_choices?.map((choice: any) => (
                                    <MenuItem
                                      key={choice.id}
                                      value={String(choice.id)}
                                    >
                                      <Checkbox
                                        checked={dynamicFields[
                                          field.label
                                        ]?.includes(String(choice.id))}
                                      />
                                      <ListItemText primary={choice.choice} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            {field.field_type === "tel-phone" && (
                              <PhoneInput
                                defaultCountry="IN"
                                value={dynamicFields[field.label] || "+91"}
                                onChange={(value) =>
                                  handleInputChange(field.label, value)
                                }
                                required={field.is_required}
                                disabled={isFieldDisabled(field?.is_editable)}
                              />
                            )}
                            {errors[field.label] && (
                              <Typography
                                variant="caption"
                                sx={{ color: "error.main", mt: 0.5, display: "block" }}
                              >
                                {capitalizeFirstLetter(errors[field.label])}
                              </Typography>
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 4,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={submitHandler}
                      disabled={!isEnabled || isButtonDisabled}
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                        py: 1.25,
                        background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                        boxShadow: "0 4px 14px rgba(99, 102, 241, 0.4)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                          boxShadow: "0 6px 20px rgba(99, 102, 241, 0.5)",
                        },
                      }}
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Paper>

                {/* Teams card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "14px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <FontAwesomeIcon icon={faUsers} style={{ fontSize: 18, color: "#64748b" }} />
                      Teams
                    </Typography>
                    {/* <Typography
                      component="button"
                      type="button"
                      sx={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: "#6366f1",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      MANAGE TEAMS
                    </Typography> */}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.5,
                      alignItems: "center",
                    }}
                  >
                    {theTeams.map((team, index) => (
                      <Box
                        key={team.id}
                        sx={{
                          px: 2,
                          py: 0.75,
                          borderRadius: "20px",
                          backgroundColor:
                            TEAM_PILL_COLORS[index % TEAM_PILL_COLORS.length],
                          color: "#334155",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                        }}
                      >
                        {team.name}
                      </Box>
                    ))}
                  
                  </Box>
                </Paper>
              </Box>
            </Box>

            {/* Right sidebar: Current Plan card */}
            {/* <Paper
              elevation={0}
              sx={{
                width: { xs: "100%", md: 340 },
                flexShrink: 0,
                borderRadius: "14px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
                  color: "#fff",
                  p: 3,
                  position: "relative",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    opacity: 0.95,
                  }}
                >
                  CURRENT PLAN
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.35rem",
                    fontWeight: 700,
                    mt: 0.5,
                  }}
                >
                  CircleOne - {planName ? planName : "Standard"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    opacity: 0.9,
                    mt: 1,
                  }}
                >
                  Unlock the full power of automation and advanced campaign analytics.
                </Typography>
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} style={{ fontSize: 14 }} />
                </Box>
              </Box>
              <Box sx={{ p: 3, flex: 1 }}>
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#334155",
                      mb: 0.75,
                    }}
                  >
                    Campaigns Used
                  </Typography>
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#e2e8f0",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: "60%",
                        borderRadius: 4,
                        background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    2 campaigns remaining this month
                    <i className="bi bi-info-circle" style={{ fontSize: 12 }} />
                  </Typography>
                </Box>
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#334155",
                      mb: 0.75,
                    }}
                  >
                    Team Members
                  </Typography>
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#e2e8f0",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: "80%",
                        borderRadius: 4,
                        bgcolor: "#22c55e",
                      }}
                    />
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    color: "#64748b",
                    mb: 1.5,
                  }}
                >
                  WHY UPGRADE?
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    p: 0,
                    listStyle: "none",
                    "& li": {
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: "0.875rem",
                      color: "#475569",
                      mb: 1,
                    },
                  }}
                >
                  <li>
                    <FontAwesomeIcon icon={faCheck} style={{ color: "#6366f1", fontSize: 14 }} />
                    Unlimited Active Campaigns
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} style={{ color: "#6366f1", fontSize: 14 }} />
                    AI-driven smart assignment
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} style={{ color: "#6366f1", fontSize: 14 }} />
                    Custom Brand White-labeling
                  </li>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1.5,
                    mt: 2,
                    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    boxShadow: "0 4px 14px rgba(99, 102, 241, 0.4)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                      boxShadow: "0 6px 20px rgba(99, 102, 241, 0.5)",
                    },
                  }}
                >
                  <FontAwesomeIcon icon={faRocket} style={{ marginRight: 8, fontSize: 14 }} />
                  Upgrade to Standard
                </Button>
                <Typography
                  component="a"
                  href="#"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 2,
                    fontSize: "0.875rem",
                    color: "#6366f1",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  View Billing History
                </Typography>
              </Box>
            </Paper> */}
          </Box>
        </Box>
      )}
    </>
  );
};

export default MyProfile;
